import { useAuth } from '../contexts/AuthContext';
import { Mail, Phone, Briefcase, Building, Calendar, Shield, IndianRupee, Award } from 'lucide-react';
import AchievementsSection from '../components/AchievementsSection';
import OffboardingLettersSection from '../components/OffboardingLettersSection';
import OffboardingSection from '../components/OffboardingSection';
import TrustedDevicesSection from '../components/TrustedDevicesSection';
import LetterSignatureSection from '../components/LetterSignatureSection';
import EmployeeAssetsSection from '../components/EmployeeAssetsSection';

const seniorityFor = { L1: 'Junior', L2: 'Associate', L3: 'Mid', L4: 'Senior', L5: 'Lead/Principal' };

function inr(n) { if (n == null) return '—'; return `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`; }

export default function Profile() {
  const { user } = useAuth();
  if (!user) return null;
  const ss = user.salary_structure;
  return (
    <div className="space-y-6" data-testid="profile-page">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Your account</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mt-1">Profile</h1>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-8">
        <div className="flex items-center gap-5">
          <div className="w-20 h-20 rounded-2xl bg-[#3D4A8E] text-white flex items-center justify-center font-heading text-3xl font-bold">
            {user.name?.[0]?.toUpperCase()}
          </div>
          <div>
            <div className="font-heading text-2xl font-bold text-slate-900">{user.name}</div>
            <div className="text-slate-500">{user.title || '—'}</div>
            <div className="mt-2 flex gap-2">
              <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full bg-[#3D4A8E]/10 text-[#3D4A8E]">
                <Shield className="w-3 h-3" /> {user.role}
              </div>
              {user.grade && (
                <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                  <Award className="w-3 h-3" /> {user.grade} · {seniorityFor[user.grade]}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-8">
          {[
            { icon: Mail, label: 'Email', value: user.email },
            { icon: Phone, label: 'Phone', value: user.phone || '—' },
            { icon: Building, label: 'Department', value: user.department || 'Unassigned' },
            { icon: Briefcase, label: 'Title', value: user.title || '—' },
            { icon: Calendar, label: 'Joined', value: user.joined_at || '—' },
            { icon: IndianRupee, label: 'Annual CTC', value: inr(user.annual_ctc) },
          ].map((it, i) => {
            const Icon = it.icon;
            return (
              <div key={i} className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl">
                <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center text-slate-500 border border-slate-200">
                  <Icon className="w-4 h-4" strokeWidth={1.8} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-bold">{it.label}</div>
                  <div className="text-sm font-semibold text-slate-900 mt-0.5">{it.value}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {ss && (
        <div className="bg-white border border-slate-200 rounded-2xl p-8" data-testid="salary-card">
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Salary breakdown</div>
          <h2 className="font-heading text-xl font-semibold text-slate-900 mt-1 mb-5">Per India new regime (FY 2025-26)</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            <Mini label="Monthly gross" v={inr(ss.monthly_gross)} />
            <Mini label="Monthly net (take-home)" v={inr(ss.monthly_net)} accent="text-emerald-700" />
            <Mini label="Monthly TDS" v={inr(ss.monthly_tax)} accent="text-red-600" />
            <Mini label="Annual tax" v={inr(ss.annual_tax)} />
            <Mini label="Basic" v={inr(ss.monthly_basic)} />
            <Mini label="HRA" v={inr(ss.monthly_hra)} />
            <Mini label="Allowances" v={inr(ss.monthly_allowances)} />
            <Mini label="Std deduction (annual)" v={inr(ss.standard_deduction)} />
          </div>
        </div>
      )}

      {/* Offboarding / Resignation card — placed prominently so it's reachable for ALL roles (employee, HR, RM, admin). */}
      {!user.terminated && (
        <OffboardingSection employeeId={user.id} employeeName={user.name} isSelf={true} canManage={false} />
      )}

      {/* My company assets — visible to every authenticated user */}
      <EmployeeAssetsSection employeeId={user.id} employeeName={user.name} canManage={false} terminated={!!user.terminated} />

      <AchievementsSection />

      {/* HR / Admin only — letter signature settings (auto-hidden for other roles) */}
      <LetterSignatureSection />

      {/* Trusted devices — visible regardless of MFA state */}
      <TrustedDevicesSection />

      {user.terminated && (
        <OffboardingLettersSection employeeId={user.id} employeeName={user.name} terminated={true} />
      )}
    </div>
  );
}

const Mini = ({ label, v, accent = 'text-slate-900' }) => (
  <div>
    <div className="text-xs uppercase tracking-wider text-slate-500 font-bold">{label}</div>
    <div className={`font-heading text-xl font-bold mt-1 ${accent}`}>{v}</div>
  </div>
);
